import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import UFOSegment from "@atlassian/wac-ufo-segment";

import ContentBlock from "@/components/wpl/content-block/src";
import { useLocalizedHref } from "@/libraries/web-framework";

import { useLocalizedImage } from "../use-localized-image";

const heroContainerStyles = css({
    "padding": "90px 0 64px",
    "display": "flex",
    "flexDirection": "row",
    "justifyContent": "center",
    "alignItems": "center",
    "minHeight": 0,
    "maxWidth": "fit-content",
    "margin": "0 auto",
    "gap": "32px",
    [media.below.md]: {
        flexDirection: "column",
        padding: "50px 0 30px"
    },

    ".textContainerStyles": {
        [media.below.md]: {
            ".contentBlock-linkFlex": {
                marginBlockEnd: "0"
            }
        },
        "h1": {
            color: "#101214",
            fontSize: "3rem",
            width: "466px",
            fontWeight: 800,
            lineHeight: "48px",
            WebkitFontSmoothing: "antialiased",
            margin: "0 0 8px 0 !important",
            [media.only.sm]: {
                width: "648px",
                fontSize: "3.75rem",
                lineHeight: "4.375rem"
            },
            [media.below.sm]: {
                width: "312px",
                fontSize: "2.5rem",
                lineHeight: "2.875rem"
            }
        },

        "p": {
            color: "#101214",
            width: "466px",
            fontWeight: 400,
            fontSize: "1rem",
            lineHeight: "24px",
            WebkitFontSmoothin: "antialiased",
            margin: "0 0 16px 0",
            [media.only.sm]: {
                width: "648px"
            },
            [media.below.sm]: {
                width: "312px"
            }
        },

        "a": {
            margin: "0"
        },

        ".text-section": {
            "padding": "0 0 0 18px",
            "& > *": {
                margin: "0 0 16px 0"
            }
        }
    }
});

const imageContainerStyles = css({
    position: "relative"
});

const imageStyles = css({
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    textAlign: "center",
    width: "668px",
    maxWidth: "100%",
    [media.below.md]: {
        width: "648px"
    },
    [media.below.sm]: {
        width: "311px"
    }
});

export const HeroSectionImage = () => {
    const intl = useIntl();

    const heroImageAltText = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.Hero.ImageAlt",
        defaultMessage:
            "List view feature in Jira showing a task for the marketing team"
    });

    return (
        <div css={imageContainerStyles}>
            <img
                alt={heroImageAltText}
                css={imageStyles}
                src={useLocalizedImage("heroImage")}
                loading="lazy"
            />
        </div>
    );
};

export const HeroSection = () => {
    const intl = useIntl();

    const heroHeaderText = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.Hero.Header",
        defaultMessage: "Project management features for all teams"
    });

    const heroBodyText = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.Hero.Body",
        defaultMessage:
            "Built for every team, Jira is how modern organizations take work from to-do to done."
    });

    const buttonText = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.Cta.TryJiraFree",
        defaultMessage: "Try Jira free"
    });

    return (
        <UFOSegment name="hero">
            <div css={heroContainerStyles}>
                <ContentBlock
                    headerText={heroHeaderText}
                    headerType="h1"
                    bodyText={heroBodyText}
                    className="textContainerStyles"
                    ctaProps={{
                        buttonProps: {
                            value: buttonText,
                            href: useLocalizedHref("/software/jira/free"),
                            variant: "primary",
                            color: "blue",
                            dangerouslySetInnerHTML: { __html: buttonText },
                            dataEventProps: {
                                "data-product-key": "jiraSoftware",
                                "data-event": "clicked",
                                "data-event-container": "heroSection",
                                "data-event-component": "linkButton"
                            }
                        }
                    }}
                />
                <HeroSectionImage />
            </div>
        </UFOSegment>
    );
};
